import React, { useState, useEffect, useRef, useCallback } from "react";
import { findCards } from "../../shared";

/**
 * Find a card to put in a slot.
 *
 * A grid of art in the middle of the screen, not a list of thumbnails in a
 * corner. Choosing which print goes on a page is a decision about how the page
 * will look, so the art has to be big enough to actually judge — a 28px
 * thumbnail tells you a VMAX from a GX and nothing about whether two alt arts
 * sit well next to each other.
 *
 * The same lookup the stack box uses: names and pictures, no prices, so every
 * print of a name comes back at once.
 */

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

export default function SlotPicker({ slot, onPick, onClose }) {
  const [term, setTerm] = useState("");
  const [hits, setHits] = useState([]);
  const [state, setState] = useState("idle");
  const [artless, setArtless] = useState(() => new Set());
  const boxRef = useRef(null);
  const runRef = useRef(0);

  useEffect(() => { boxRef.current?.focus(); }, []);

  // Escape closes it, the way every other overlay in the app behaves.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const q = term.trim();
    if (q.length < MIN_CHARS) { setHits([]); setState("idle"); return undefined; }
    const run = ++runRef.current;
    setState("loading");
    const t = setTimeout(async () => {
      try {
        const found = await findCards(q);
        if (run !== runRef.current) return;
        setHits(found);
        setState("ready");
      } catch {
        if (run === runRef.current) setState("error");
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [term]);

  const noArt = useCallback(
    (id) => setArtless((s) => (s.has(id) ? s : new Set(s).add(id))),
    []
  );

  return (
    <>
      {/* Clicking away closes it. A modal you can only leave by finding the
          right button is a modal people feel trapped in. */}
      <div className="bnd-scrim" onClick={onClose} aria-hidden="true" />

      <div className="bnd-picker" role="dialog" aria-modal="true" aria-label="Choose a card for this slot">
        <div className="bnd-picker-head">
          <input
            ref={boxRef}
            className="bnd-picker-input"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search a card — Umbreon, Charizard ex…"
            aria-label="Find a card"
            spellCheck={false}
          />
          <button type="button" className="bnd-picker-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="bnd-picker-body">
          {state === "idle" && term.trim().length < MIN_CHARS && (
            <p className="bnd-picker-blank">
              Type a name. Every print comes back, so you can see how each one looks
              before it goes on the page.
            </p>
          )}
          {state === "loading" && <p className="msg-empty">Looking…</p>}
          {state === "error" && <p className="msg-error">Could not search just now.</p>}
          {state === "ready" && hits.length === 0 && (
            <p className="msg-empty">Nothing called “{term.trim()}”. Check the spelling.</p>
          )}

          {hits.length > 0 && (
            <ul className="bnd-picker-list">
              {hits.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    title={`${c.name} — ${c.setName}${c.cardNumber ? ` ${c.cardNumber}` : ""}`}
                    onClick={() =>
                      onPick(slot, {
                        id: c.id, name: c.name, setName: c.setName,
                        number: c.cardNumber, image: c.imageUrl || null,
                      })
                    }
                  >
                    <span className="bnd-picker-art">
                      {c.imageUrl && !artless.has(c.id) ? (
                        /* Not lazy: the art is the whole reason this is on
                           screen, and inside an aspect-ratio box the browser
                           never decides a lazy image is visible at all. */
                        <img src={c.imageUrl} alt="" onError={() => noArt(c.id)} />
                      ) : (
                        <span className="bnd-picker-noart">{c.name}</span>
                      )}
                    </span>
                    <span className="bnd-picker-label">
                      {c.name}
                      <span className="bnd-picker-set">
                        {c.setName}
                        {c.cardNumber && ` · ${c.cardNumber}`}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {hits.length > 0 && (
          <p className="bnd-picker-count">
            {hits.length} print{hits.length === 1 ? "" : "s"} — pick one to put it in the slot
          </p>
        )}
      </div>
    </>
  );
}
